import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Star, Send, Users, CalendarCheck, Loader2, Trash2, Settings2, ExternalLink, BellOff, BellRing } from "lucide-react";
import { ReviewRequestDialog } from "@/components/ReviewRequestDialog";
import { ReviewSettingsForm } from "@/components/reviews/ReviewSettingsForm";

export default function ReviewsPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogCustomer, setDialogCustomer] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/reviews/summary`);
      setSummary(res.data);
    } catch {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this review request from history?")) return;
    try {
      await axios.delete(`${API}/reviews/requests/${id}`);
      toast.success("Review request removed");
      load();
    } catch (err) {
      if (err.response?.status === 202) toast.info("Deletion sent to owner for approval");
      else toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const handleAction = async (id, action, body) => {
    try {
      await axios.post(`${API}/reviews/requests/${id}/${action}`, body || {});
      toast.success(action === "mark-reviewed" ? "Marked as reviewed" : "Reminders updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reviews-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Star className="w-6 h-6 text-amber-500 fill-amber-500" /> Google Reviews
          </h1>
          <p className="text-slate-600 mt-1">Track review requests and manage your closeout message.</p>
        </div>
        {summary?.review_url && (
          <a href={summary.review_url} target="_blank" rel="noreferrer" className="text-sm text-indigo-600 hover:underline flex items-center gap-1" data-testid="reviews-link">
            Review link <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Send} label="Sent this month" value={summary?.sent_this_month} color="bg-indigo-50 text-indigo-600" testId="stat-sent-month" />
        <StatCard icon={CalendarCheck} label="Sent all time" value={summary?.sent_total} color="bg-emerald-50 text-emerald-600" testId="stat-sent-total" />
        <StatCard icon={Star} label="Marked reviewed" value={summary?.reviewed_total} color="bg-amber-50 text-amber-600" testId="stat-reviewed" />
        <StatCard icon={Users} label="Completed, not asked" value={summary?.not_asked_count} color="bg-rose-50 text-rose-600" testId="stat-not-asked" />
      </div>

      <Tabs defaultValue="pending">
        <TabsList className="w-full sm:w-auto grid grid-cols-3 sm:inline-flex h-auto">
          <TabsTrigger value="pending" className="text-xs sm:text-sm" data-testid="tab-not-asked">Not Asked ({summary?.not_asked_count || 0})</TabsTrigger>
          <TabsTrigger value="history" className="text-xs sm:text-sm" data-testid="tab-history">History</TabsTrigger>
          <TabsTrigger value="settings" className="text-xs sm:text-sm" data-testid="tab-settings"><Settings2 className="w-3.5 h-3.5 mr-1 hidden sm:inline" /> Message &amp; Auto-Send</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Customers with a completed project who haven't been asked</CardTitle>
              <CardDescription>
                Auto-send is {summary?.auto_enabled ? `ON (${summary.auto_delay_days === 0 ? "same day" : `${summary.auto_delay_days} day(s) after completion`})` : "OFF"}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {summary?.not_asked?.length ? (
                <div className="divide-y">
                  {summary.not_asked.map(c => (
                    <div key={c.lead_id} className="flex items-center justify-between py-3 gap-3" data-testid={`not-asked-${c.lead_id}`}>
                      <div className="min-w-0">
                        <button className="font-medium text-sm text-slate-900 hover:text-indigo-600 text-left" onClick={() => navigate(`/customers/${c.lead_id}`)}>
                          {c.first_name} {c.last_name}
                        </button>
                        <p className="text-xs text-slate-500 truncate">{c.project_name || "Project"} · completed {c.completed_at ? new Date(c.completed_at).toLocaleDateString() : "—"}</p>
                      </div>
                      <Button size="sm" onClick={() => setDialogCustomer(c)} data-testid={`ask-review-${c.lead_id}`}>
                        <Star className="w-3.5 h-3.5 mr-1" /> Request Review
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 py-8 text-center">Everyone with a completed project has been asked. Nice work!</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardContent className="pt-4">
              {summary?.recent?.length ? (
                <div className="divide-y">
                  {summary.recent.map(r => (
                    <div key={r.request_id} className="flex items-center justify-between py-3 gap-3" data-testid={`history-${r.request_id}`}>
                      <div className="min-w-0">
                        <button className="font-medium text-sm text-slate-900 hover:text-indigo-600 text-left" onClick={() => r.customer_id && navigate(`/customers/${r.customer_id}`)}>
                          {r.customer_name || "Customer"}
                        </button>
                        <p className="text-xs text-slate-500">
                          {new Date(r.sent_at).toLocaleString()} · {r.source === "manual" ? "Manual" : "Automatic"}
                          {r.reminder_count > 0 && ` · ${r.reminder_count} reminder${r.reminder_count > 1 ? "s" : ""}`}
                          {r.reminders_off && !r.reviewed_at && " · reminders off"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                        {r.reviewed_at ? (
                          <Badge className="bg-amber-100 text-amber-800" data-testid={`reviewed-badge-${r.request_id}`}><Star className="w-3 h-3 mr-1 fill-amber-600 text-amber-600" /> Reviewed</Badge>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleAction(r.request_id, "mark-reviewed")} data-testid={`mark-reviewed-${r.request_id}`}>Mark reviewed</Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => handleAction(r.request_id, "reminders", { enabled: !!r.reminders_off })} data-testid={`toggle-reminders-${r.request_id}`}>
                              {r.reminders_off ? <><BellRing className="w-3 h-3 mr-1" /> Resume reminders</> : <><BellOff className="w-3 h-3 mr-1" /> Stop reminders</>}
                            </Button>
                          </>
                        )}
                        {r.email !== null && r.email !== undefined && <Badge className={r.email_sent ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>Email {r.email_sent ? "sent" : "failed"}</Badge>}
                        {r.phone !== null && r.phone !== undefined && <Badge className={r.sms_sent ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>SMS {r.sms_sent ? "sent" : "failed"}</Badge>}
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => handleDelete(r.request_id)} data-testid={`delete-${r.request_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 py-8 text-center">No review requests sent yet.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <ReviewSettingsForm isOwner={user?.role === "owner"} onSaved={load} />
        </TabsContent>
      </Tabs>

      <ReviewRequestDialog
        open={!!dialogCustomer}
        onOpenChange={(o) => { if (!o) { setDialogCustomer(null); load(); } }}
        customer={dialogCustomer}
      />
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="pt-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value ?? 0}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </CardContent>
  </Card>
);
